import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { Badge, Code, Table } from "@mantine/core"
import { Fragment } from "react"
import { formatTime, formatTimeWithSeconds } from "../../lib/format-date"
import type { IProps } from "./interfaces"
import classes from "./styles.module.css"

function severityColor(severity: string): string {
  if (severity === "error") return "danger"
  if (severity === "warn") return "warning"
  if (severity === "info") return "info"
  return "gray"
}

/**
 * DiagnosticEventsTable — the shared render surface for a list of session-event
 * rows (Time / Component / Type / Severity / Source / Message). Used by both the
 * system-wide diagnostics view and the per-company diagnostics page, in three
 * spots: the expandable events tables (click a row to reveal its JSON context)
 * and the static meeting-timeline table. A dumb component: rows and callbacks in,
 * no data fetching.
 */
export const DiagnosticEventsTable = ({
  events,
  expandedEventId = null,
  onToggleEvent,
  withSeconds = false,
}: IProps): React.JSX.Element => {
  const { i18n } = useLingui()
  const expandable = typeof onToggleEvent === "function"

  return (
    <Table highlightOnHover={expandable}>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>
            <Trans>Time</Trans>
          </Table.Th>
          <Table.Th>
            <Trans>Component</Trans>
          </Table.Th>
          <Table.Th>
            <Trans>Type</Trans>
          </Table.Th>
          <Table.Th>
            <Trans>Severity</Trans>
          </Table.Th>
          <Table.Th>
            <Trans>Source</Trans>
          </Table.Th>
          <Table.Th>
            <Trans>Message</Trans>
          </Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {events.map((ev) => (
          <Fragment key={ev.id}>
            <Table.Tr
              className={expandable ? classes.clickableRow : undefined}
              onClick={expandable ? () => onToggleEvent?.(ev.id) : undefined}
            >
              <Table.Td className={classes.timeCell}>
                {withSeconds ? formatTimeWithSeconds(i18n, ev.occurred_at) : formatTime(i18n, ev.occurred_at)}
              </Table.Td>
              <Table.Td>
                <Code>{ev.component}</Code>
              </Table.Td>
              <Table.Td>{ev.event_type}</Table.Td>
              <Table.Td>
                <Badge color={severityColor(ev.severity)} variant="light" size="sm">
                  {ev.severity}
                </Badge>
              </Table.Td>
              <Table.Td>{ev.source}</Table.Td>
              <Table.Td className={classes.messageCell}>{ev.message}</Table.Td>
            </Table.Tr>
            {expandable && expandedEventId === ev.id && (
              <Table.Tr>
                <Table.Td colSpan={6}>
                  <Code block className={classes.contextCode}>
                    {JSON.stringify(ev.context, null, 2) ?? "null"}
                  </Code>
                </Table.Td>
              </Table.Tr>
            )}
          </Fragment>
        ))}
      </Table.Tbody>
    </Table>
  )
}
